import { unformatSnils, validateSnils } from "utils/snils";

import { apiRTK } from "./apiRTK";
import { store } from "./store";

/**
 *
 *
 * FORM CONSTANTS
 *
 *
 */

// Для выбора часового пояса
export const timezoneOptions = {
  2: { location: "Калининград", gmt: "GMT+2" },
  3: { location: "Москва, Волгоград, Киров", gmt: "GMT+3" },
  4: { location: "Самара, Саратов", gmt: "GMT+4" },
  5: { location: "Екатеринбург", gmt: "GMT+5" },
  6: { location: "Омск", gmt: "GMT+6" },
  7: { location: "Новосибирск, Красноярск, Барнаул", gmt: "GMT+7" },
  8: { location: "Иркутск", gmt: "GMT+8" },
  9: { location: "Чита, Якутск", gmt: "GMT+9" },
  10: { location: "Владивосток", gmt: "GMT+10" },
  11: { location: "Магадан, О-в Сахалин, Среднеколымск", gmt: "GMT+11" },
  12: { location: "Петропавловск-Камчатский, Анадырь", gmt: "GMT+12" },
};

export const VALIDATE_DEBOUNCE_MS = 1000;
export const NAME_REG_EXP = /^[\sа-яёА-ЯЁa-zA-Z-]{2,30}$/;
export const POSITION_REG_EXP =
  /^[а-яёА-ЯЁa-zA-Z](?:[-\s]?[а-яёА-ЯЁa-zA-Z]){1,49}$/;
export const NEW_NAME_REG_EXP =
  /^[а-яёА-ЯЁa-zA-Z](?:[-\s]?[а-яёА-ЯЁa-zA-Z]){1,29}$/;
export const PASSWORD_REG_EXP =
  /^(?=[^а-яёА-ЯЁ]*[0-9])(?=[^а-яёА-ЯЁ]*[A-Z])[^а-яёА-ЯЁ]{8,}$/;
export const EMAIL_REG_EXP = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
export const SYST_REG_EXP = /^(9\d|1([0-5]\d|60))$/; // 90-160
export const DIAST_REG_EXP = /^(6\d|[7-9]\d|100)$/; // 60-100
export const PULSE_REG_EXP = /^(4\d|[5-9]\d|100)$/; // 40-100

export const EXCEL_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroenabled.12",
];

// systLower: 100
// systUpper: 140
// diastLower: 65
// diastUpper: 90
// pulseLower: 60
// pulseUpper: 90

export const medReferenceData = {
  systLower: 100,
  systUpper: 140,
  diastLower: 65,
  diastUpper: 90,
  pulseLower: 60,
  pulseUpper: 90,
};

export const medRoomReferenceData = {
  ...medReferenceData,
  luminosityLower: 200,
  luminosityUpper: 700,
  temperatureLower: 20,
  temperatureUpper: 35,
  moistureLower: 30,
  moistureUpper: 85,
};

// Этапы измерений для фото
// // Совмещенный этап алкоголя и термометра
// export const stagesPhotoAlcoJoinTemp: Record<number, string> = {
//   0: "Этап авторизации пользователя",
//   1: "Этап измерения давления",
//   2: "Этап измерения уровня этанола в выдыхаемом воздухе и температуры тела",
// };
// раздельный
export const stagesPhoto: Record<number, string> = {
  0: "Этап авторизации пользователя",
  10: "Этап измерения давления",
  20: "Этап измерения уровня этанола и температуры тела",
  21: "Этап измерения уровня этанола",
  22: "Этап измерения температуры тела",
};

export const minStagesPhoto: Record<number, string> = {
  0: "Авторизация",
  10: "Давление",
  20: "Этанол и температура",
  21: "Уровень этанола",
  22: "Температура тела",
};

// Получаем этап измерения для фото
export const getStagePhoto = (stage: number) => stagesPhoto[stage];

export const getMinStagePhoto = (stage: number) => minStagesPhoto[stage];

export const getUserNameRules = (
  userName: string | null | undefined = undefined,
  isRequired: boolean = true
) => {
  const rule = [
    {
      required: true,
      message: "Обязательно к заполнению",
    },
    {
      message:
        "Допускается ввод латинских букв, цифр и спец. символов. Ограничение по длине от 4 до 30 символов",
      pattern: /^[!-~]{4,30}$/,
    },
    {
      validator: (_: any, value: string) => {
        if (!value?.length) {
          return Promise.resolve();
        }

        if (userName && userName === value) {
          return Promise.resolve();
        }

        const encodeValue = encodeURIComponent(value);

        return new Promise(async (resolve, reject) => {
          const promise = store.dispatch(
            apiRTK.endpoints.getV2UsersByNameIsUnique.initiate(encodeValue, {
              forceRefetch: true,
            })
          );
          const { data: isUnique } = await promise;

          if (isUnique) {
            resolve(1);
          } else {
            reject();
          }
        });
      },
      message: "Пользователь с таким логином уже существует",
    },
  ];

  return isRequired ? rule : rule.slice(1);
};

export const getPasswordRules = (isRequired: boolean = true) => {
  const rule = [
    {
      required: true,
      message: "Обязательно к заполнению",
    },
    {
      message:
        "Пароль должен состоять не менее, чем из 8 символов, среди которых должны быть минимум 1 заглавная латинская буква и 1 арабская цифра",
      pattern: PASSWORD_REG_EXP,
    },
  ];

  return isRequired ? rule : rule.slice(1);
};

export const getSnilsRules = (currentSnils?: string | null | undefined) => {
  return [
    { required: true, message: "Обязательно к заполнению" },
    {
      validator: (_: any, value: string) => {
        if (!value || value.trim() === "") {
          return Promise.resolve();
        }

        // Если initialValue изменили и вернули обратно, чтобы не ругался на неуникальность
        const unFormatValue = value && unformatSnils(value);
        if (currentSnils && currentSnils === unFormatValue) {
          return Promise.resolve();
        }

        const error = validateSnils(value);
        if (error) {
          return Promise.reject(new Error(error));
        }

        return new Promise(async (resolve, reject) => {
          const promise = store.dispatch(
            apiRTK.endpoints.getV2PersonsSnilsBySnilsIsUnique.initiate(
              unFormatValue,
              {
                forceRefetch: true,
              }
            )
          );
          const { data: isUnique } = await promise;

          if (isUnique) {
            resolve(1);
          } else {
            reject(new Error("Пользователь с таким СНИЛС уже существует"));
          }
        });
      },
    },
  ];
};

export const getPersonalNumberRules = (
  currentPersonalNumber?: string | null | undefined
) => {
  return [
    {
      message:
        "Табельный номер не может содержать кириллические буквы, ограничение по длине - 50 символов",
      pattern: /^[^а-яёА-ЯЁ]{1,50}$/,
    },
    {
      validator: (_: any, value: string) => {
        if (!value?.length) {
          return Promise.resolve();
        }

        if (currentPersonalNumber && currentPersonalNumber === value) {
          return Promise.resolve();
        }

        return new Promise(async (resolve, reject) => {
          const promise = store.dispatch(
            apiRTK.endpoints.getV2PersonsPersonalNumberByPersonalNumberIsUnique.initiate(
              value,
              { forceRefetch: true }
            )
          );
          const { data: isUnique } = await promise;

          if (isUnique) {
            resolve(1);
          } else {
            reject();
          }
        });
      },
      message: "Пользователь с таким персональным номером уже существует",
    },
  ];
};

export const getEmailRules = (isRequired?: boolean) => {
  const rule = [
    {
      required: true,
      message: "Обязательно к заполнению",
    },
    {
      message: "Допускается ввод корректного email адреса",
      pattern: EMAIL_REG_EXP,
    },
    {
      message: "Ограничение по длине - 50 символов",
      max: 50,
    },
  ];

  return isRequired ? rule : rule.slice(1);
};

export const getOgrnRules = () => {
  return [
    {
      required: true,
      message: "Обязательно к заполнению",
    },
    {
      message:
        "Допускается ввод корректного ОГРН, ограничение по длине - 13  символов ",
      pattern: /^\d{13}$/,
    },
  ];
};
